import { Injectable } from "@angular/core";
import { IGraph } from "@yfiles/yfiles";
import type { jsPDF } from "jspdf";
import { LinqYFilesExportService } from "../canvas/yfiles-canvas-export.service";
import { convertModernColours } from "./pdf-colours";
import { fitImageToPage, placeOnPage } from "./pdf-layout";

export const PDFMARGIN:number = 5

// jsPDF and html2canvas are only loaded when something is exported, so they aren't part of the app's start-up bundle
export const loadJsPDF = async () => (await import("jspdf")).default

@Injectable({
    providedIn: 'root'
})
export class PdfService {
    constructor(private exportService:LinqYFilesExportService) {}

    async createPDF(orientation:'p'|'l') {
        const JsPDF = await loadJsPDF()
        return new JsPDF({
            orientation: orientation,
            unit: 'mm',
            format: 'a4'
        });
    }

    // html2canvas copies the page before drawing the element, and copying pages with graphs and charts can take minutes.
    // Only the element and its ancestors are copied (the head, with its styles, is kept), and the element's width is
    // pinned so its layout doesn't change without the rest of the page. To put many parts of one element in a PDF,
    // capture it once and add slices of the canvas (see captureSections). onClone is given the copy of the element, laid
    // out as it will be drawn
    async captureElement(element:HTMLElement, scale:number, onClone?:(clone:HTMLElement) => void) {
        const previousWidth = element.style.width
        element.style.width = `${element.getBoundingClientRect().width}px`
        try {
            const html2canvas = (await import("html2canvas")).default
            return await html2canvas(element, {
                scale,
                backgroundColor: '#ffffff',
                ignoreElements: (e:Element) => document.body.contains(e) && !e.contains(element) && !element.contains(e),
                onclone: (clone:Document, cloned:HTMLElement) => {
                    convertModernColours(clone)
                    onClone?.(cloned)
                },
            })
        }finally {
            element.style.width = previousWidth
        }
    }

    // captures the element once and returns a slice of the image for each of its parts that `select` picks, top to
    // bottom. The parts are measured in html2canvas's copy, not the page: the copy's text can wrap a little differently,
    // so parts lower down drift by a line or two, and slices measured on the page would cut their tops off
    async captureSections(element:HTMLElement, scale:number, select:(root:HTMLElement) => HTMLElement[]) {
        let bands:{ top:number, bottom:number }[] = []
        const canvas = await this.captureElement(element, scale, clone => {
            const rootBox = clone.getBoundingClientRect()
            bands = select(clone).map(part => {
                const box = part.getBoundingClientRect()
                return { top: box.top - rootBox.top, bottom: box.bottom - rootBox.top }
            })
        })
        const pixelsPerCssPixel = canvas.width / element.getBoundingClientRect().width
        return bands.map(band => this.sliceCanvas(canvas, band.top * pixelsPerCssPixel, band.bottom * pixelsPerCssPixel))
    }

    // a horizontal band of a canvas, from top to bottom in canvas pixels
    sliceCanvas(canvas:HTMLCanvasElement, top:number, bottom:number) {
        const height = Math.max(1, Math.round(bottom - top))
        const slice = document.createElement('canvas')
        slice.width = canvas.width
        slice.height = height
        slice.getContext('2d')?.drawImage(canvas, 0, Math.round(top), canvas.width, height, 0, 0, canvas.width, height)
        return slice
    }

    // adds a canvas across the page below the current position (mm from the top), starting a new page if it doesn't
    // fit; returns the position of its bottom
    addCanvasToPdf(pdf:jsPDF, canvas:HTMLCanvasElement, current?:number) {
        const pageWidth = pdf.internal.pageSize.getWidth()-(PDFMARGIN*2);
        const pageHeight = pdf.internal.pageSize.getHeight()-(PDFMARGIN*2);

        if (!(canvas?.height > 0) || !(canvas.width > 0)) return current

        const imgHeight = (canvas.height * pageWidth) / canvas.width;

        const place = placeOnPage(current, imgHeight, pdf.internal.pageSize.getHeight(), PDFMARGIN)
        if (place.newPage) pdf.addPage()

        // taller than a page: continue it over as many pages as it needs rather than running off the bottom
        if (place.sliced) return this.addSlicedCanvas(pdf, canvas, pageWidth, pageHeight)

        pdf.addImage(canvas, 'JPEG', PDFMARGIN, place.top, pageWidth, imgHeight, undefined, "MEDIUM");

        return place.top + imgHeight
    }

    // from the top of the current page, a page's height at a time
    private addSlicedCanvas(pdf:jsPDF, canvas:HTMLCanvasElement, pageWidth:number, pageHeight:number) {
        const pixelsPerMm = canvas.width / pageWidth
        const slicePixels = Math.max(1, Math.floor(pageHeight * pixelsPerMm))
        let bottom = PDFMARGIN

        for (let y = 0; y < canvas.height; y += slicePixels) {
            if (y > 0) pdf.addPage()
            const height = Math.min(slicePixels, canvas.height - y)
            const slice = this.sliceCanvas(canvas, y, y + height)
            pdf.addImage(slice, 'JPEG', PDFMARGIN, PDFMARGIN, pageWidth, height / pixelsPerMm, undefined, "MEDIUM")
            bottom = PDFMARGIN + height / pixelsPerMm
        }
        return bottom
    }

    // the graph on a page of its own, turned to suit its shape: added to a PDF, or as a new one
    async generateGraph(graph:IGraph, pdf?:jsPDF) {
        const png = await this.exportService.createPNG(graph)
        const orientation = png.width > png.height ? 'landscape' : 'portrait'

        const newPdf = pdf || new (await loadJsPDF())({
                orientation,
                unit: 'mm',
                format: 'a4'
            });

        // an empty graph has nothing to show, and no size to fit to the page
        if (!(png.width > 0) || !(png.height > 0)) return newPdf

        if (pdf) newPdf.addPage('a4', orientation)

        const pageWidth = newPdf.internal.pageSize.getWidth() - (2*PDFMARGIN)
        const pageHeight = newPdf.internal.pageSize.getHeight() - (2*PDFMARGIN)

        // the image is in pixels and the page in mm, so small models print at their real size rather than a pixel per mm
        const { width: imgWidth, height: imgHeight } = fitImageToPage(png.width, png.height, pageWidth, pageHeight)

        const horizontalMargin = (pageWidth + (2*PDFMARGIN) - imgWidth) / 2
        const verticalMargin = (pageHeight + (2*PDFMARGIN) - imgHeight) / 2

        newPdf.addImage(png.png, 'PNG', horizontalMargin, verticalMargin, imgWidth, imgHeight, undefined, "MEDIUM");
        return newPdf
    }
}